import React, { useState } from 'react';
import {
  deleteGiide as deleteGiideStore,
  setCreateGiideError,
  setGiideTabState,
  updateGiide as updateGiideStore,
} from '../actions';
import { message, Modal } from 'antd';
import {
  CreateNewGiideMutation,
  DeleteGiideAllInput,
  DeleteGiideAllMutation,
  PublishState,
  UnpublishGiideInput,
  UnpublishGiideMutation,
  UpdateGiideEmbedSettingsMutation,
  UpdateGiideInput,
  UpdateGiideMutation,
} from '../graphql/api';
import { useDispatch } from 'react-redux';
import useMutation from './useMutation';
import useUser from './useUser';
import { deleteGiideAll, unpublishGiide } from '../graphql/mutations';
import gql from 'graphql-tag';
import { useHistory } from 'react-router';
import useGiideList from './useQuery/useGiideList';
import { API, graphqlOperation } from 'aws-amplify';
import usePlan from './usePlan';
import { CreateGiideErrorType } from '../types';

const updateGiideEmbedSettings = gql`
  mutation UpdateGiideEmbedSettings($input: inputUpdateGiideEmbedSettings!) {
    updateGiideEmbedSettings(input: $input) {
      id
      owner
      tenantID
      embedCodeState
      embedCodeAllowedDomains
    }
  }
`;

const updateGiideMutation = gql`
  mutation UpdateGiide($input: UpdateGiideInput!) {
    updateGiide(input: $input) {
      id
      owner
      tenantID
    }
  }
`;

const createNewGiide = gql`
  mutation CreateNewGiide {
    createNewGiide {
      giideID
      userID
      tenantID
      stepID
    }
  }
`;

const useGiideOperations = (id?) => {
  const dispatch = useDispatch();
  const [giideTitleEditing, setGiideTitleEditing] = useState<boolean>(false);
  const [giideEmbedCodeEditing, setGiideEmbedCodeEditing] =
    useState<boolean>(false);
  const { user } = useUser();
  const { push } = useHistory();
  const { refetch } = useGiideList(true);
  const [giideSaving, setGiideSaving] = useState<boolean>(false);
  const { refetch: refetchAccount } = usePlan();

  const updateMutation = useMutation<UpdateGiideInput, UpdateGiideMutation>(
    updateGiideMutation
  );
  const updateGiideEmbedSettingsMutation = useMutation<
    UpdateGiideInput,
    UpdateGiideEmbedSettingsMutation
  >(updateGiideEmbedSettings);
  const unpublishGiideMutation = useMutation<
    UnpublishGiideInput,
    UnpublishGiideMutation
  >(unpublishGiide);
  const deleteGiideAllMutation = useMutation<
    DeleteGiideAllInput,
    DeleteGiideAllMutation
  >(deleteGiideAll);

  const handleCreateNewGiide = React.useCallback(
    async (title?: string) => {
      setGiideSaving(true);

      let response;
      try {
        response = (await API.graphql(graphqlOperation(createNewGiide))) as {
          data: CreateNewGiideMutation;
        };
      } catch (e) {
        message.error('Error occurred while creating new giide');
      }
      // app crashes if this value is null
      if (response.data.createNewGiide) {
        dispatch(setGiideTabState(undefined));
        refetch(true);
        // need to get new account data to keep giide counter accurate
        refetchAccount();
        push(`/giide/${response.data.createNewGiide!.giideID}`);
      } else {
        dispatch(setCreateGiideError(CreateGiideErrorType.unknown));
      }
      setGiideSaving(false);
    },
    [push, refetch, dispatch, refetchAccount]
  );

  const updateGiideEmbedCode = React.useCallback(
    (newEmbedCodeState, newEmbedCodeAllowedDomains, current = false) => {
      setGiideEmbedCodeEditing(true);
      updateGiideEmbedSettingsMutation
        .callMutation({
          id,
          embedCodeState: newEmbedCodeState,
          embedCodeAllowedDomains: newEmbedCodeAllowedDomains,
        })
        .then(() => {
          setGiideEmbedCodeEditing(false);
          if (current)
            dispatch(
              updateGiideStore(
                {
                  id,
                  embedCodeState: newEmbedCodeState,
                  embedCodeAllowedDomains: newEmbedCodeAllowedDomains,
                },
                true
              )
            );
          else
            dispatch(
              updateGiideStore({
                id,
                embedCodeState: newEmbedCodeState,
                embedCodeAllowedDomains: newEmbedCodeAllowedDomains,
              })
            );
        });
    },
    [dispatch, id, updateGiideEmbedSettingsMutation]
  );

  const updateGiideTitle = React.useCallback(
    (newTitle, current = false) => {
      // Prevent title with zero characters
      if (newTitle.length < 1) {
        return;
      }

      setGiideTitleEditing(false);
      updateMutation
        .callMutation({ id, title: newTitle })
        .then(result => {
          setGiideTitleEditing(false);

          if (current)
            dispatch(updateGiideStore({ id, title: newTitle }, true));
          else dispatch(updateGiideStore({ id, title: newTitle }));
        })
        .catch(e =>
          message.error(
            'There was a problem updating the title for this giide.'
          )
        );
    },
    [dispatch, id, updateMutation]
  );

  const [deleteGiideModalAPI, deleteGiideModal] = Modal.useModal();

  const onDeleteGiide = React.useCallback(
    (redirectToDashboard = false) => {
      deleteGiideModalAPI.confirm({
        centered: true,
        okType: 'danger',
        okText: 'Delete',
        closable: true,
        onCancel: () => null,
        onOk: async () => {
          try {
            if (!id || !user?.tenantID) {
              throw new Error('Giide id or tenant id is missing.');
            }
            const result = await deleteGiideAllMutation.callMutation({
              giideId: id,
            });
            if (!result.data.deleteGiideAll?.success) {
              throw new Error(
                result.data.deleteGiideAll?.error || 'An error during deleting.'
              );
            }
            dispatch(deleteGiideStore(id));
            message.success('Giide was deleted.');
            if (redirectToDashboard) push('/');
            // need to get new account data to keep giide counter accurate
            refetchAccount();
          } catch (err) {
            dispatch(
              updateGiideStore({ id: id, publishState: PublishState.error })
            );
            message.error('An error during deleting.');
          }
          return true;
        },
        title: 'Delete Giide',
        content:
          'Are you sure you want to delete this giide? The audio, feed cards, and recording notes in this giide will be permanently deleted.',
      });
    },
    [
      id,
      dispatch,
      deleteGiideAllMutation,
      user?.tenantID,
      push,
      refetchAccount,
      deleteGiideModalAPI,
    ]
  );

  const onUnpublishGiide = React.useCallback(
    // ZM: changed because giideData was being lost on unpublish,
    // author and title field not autofilling in publish modal
    // OLD:
    // (current = false) => {
    // NEW:
    current => {
      Modal.confirm({
        centered: true,
        closable: true,
        // className: 'deleteModal unpublishGiideModal',
        cancelText: 'Cancel',
        okText: 'Unpublish',
        onCancel: () => null,
        onOk: async () => {
          try {
            if (!id || !user?.tenantID) {
              throw new Error('Giide id or tenant id is missing.');
            }
            const result = await unpublishGiideMutation.callMutation({
              giideId: id,
            });
            if (!result.data.unpublishGiide?.success) {
              throw new Error(
                result.data.unpublishGiide?.error ||
                  'An error during un-publishing.'
              );
            }
            if (current)
              dispatch(
                updateGiideStore(
                  { id, publishState: PublishState.unpublished },
                  current
                )
              );
            else
              dispatch(
                updateGiideStore({ id, publishState: PublishState.unpublished })
              );
            message.success('Giide was un-published.');
          } catch (err) {
            dispatch(
              updateGiideStore({ id, publishState: PublishState.error })
            );
            message.error('An error during un-publishing.');
          }
          return true;
        },
        content: 'Are you sure you want to unpublish this giide?',
        okType: 'danger',
        title: 'Confirm unpublish giide',
      });
    },
    [id, dispatch, unpublishGiideMutation, user?.tenantID]
  );

  return {
    updateGiideTitle,
    updateGiideEmbedCode,
    onDeleteGiide,
    onUnpublishGiide,
    giideTitleEditing,
    setGiideTitleEditing,
    giideEmbedCodeEditing,
    createNewGiide: handleCreateNewGiide,
    giideSaving,
    deleteGiideModal,
  };
};

export default useGiideOperations;
